const DATABASE_NAME = 'tabby-media';
const DATABASE_VERSION = 1;
const STORE_NAME = 'custom-backgrounds';
export const CUSTOM_BACKGROUND_BLOB_KEY = 'current';

const ownedObjectUrls = new Set<string>();
let cachedBlobUrl: string | null = null;

const openDatabase = (): Promise<IDBDatabase> => new Promise((resolve, reject) => {
  const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);

  request.onupgradeneeded = () => {
    const database = request.result;
    if (!database.objectStoreNames.contains(STORE_NAME)) {
      database.createObjectStore(STORE_NAME);
    }
  };
  request.onsuccess = () => resolve(request.result);
  request.onerror = () => reject(request.error ?? new Error('Unable to open media storage'));
});

const runTransaction = async <T>(
  mode: IDBTransactionMode,
  operation: (store: IDBObjectStore) => IDBRequest<T>
): Promise<T> => {
  const database = await openDatabase();

  try {
    return await new Promise<T>((resolve, reject) => {
      const transaction = database.transaction(STORE_NAME, mode);
      const request = operation(transaction.objectStore(STORE_NAME));
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error ?? new Error('Media storage operation failed'));
      transaction.onabort = () => reject(transaction.error ?? new Error('Media storage transaction aborted'));
    });
  } finally {
    database.close();
  }
};

export const saveCustomBackgroundBlob = async (blob: Blob): Promise<string> => {
  await runTransaction('readwrite', store => store.put(blob, CUSTOM_BACKGROUND_BLOB_KEY));
  cachedBlobUrl = null;
  return CUSTOM_BACKGROUND_BLOB_KEY;
};

export const getCustomBackgroundBlob = async (key = CUSTOM_BACKGROUND_BLOB_KEY): Promise<Blob | null> => {
  const value = await runTransaction<Blob | undefined>('readonly', store => store.get(key));
  return value instanceof Blob ? value : null;
};

export const deleteCustomBackgroundBlob = async (key = CUSTOM_BACKGROUND_BLOB_KEY): Promise<void> => {
  await runTransaction('readwrite', store => store.delete(key));
  cachedBlobUrl = null;
};

export const createCustomBackgroundUrl = (blob: Blob): string => {
  if (cachedBlobUrl && ownedObjectUrls.has(cachedBlobUrl)) return cachedBlobUrl;

  cachedBlobUrl = URL.createObjectURL(blob);
  ownedObjectUrls.add(cachedBlobUrl);
  return cachedBlobUrl;
};

export const releaseUnusedCustomBackgroundUrls = (activeUrls: string[] = []) => {
  const active = new Set(activeUrls);
  ownedObjectUrls.forEach(url => {
    if (!active.has(url)) {
      URL.revokeObjectURL(url);
      ownedObjectUrls.delete(url);
      if (cachedBlobUrl === url) cachedBlobUrl = null;
    }
  });
};

export const releaseAllCustomBackgroundUrls = () => {
  releaseUnusedCustomBackgroundUrls();
};

export const dataUrlToBlob = (dataUrl: string): Blob => {
  const separator = dataUrl.indexOf(',');
  if (separator < 0) throw new Error('Invalid custom background data');

  const header = dataUrl.slice(0, separator);
  const binary = atob(dataUrl.slice(separator + 1));
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return new Blob([bytes], {
    type: header.match(/^data:(.*?);base64$/)?.[1] || 'application/octet-stream',
  });
};
